import { API_BASE } from '../lib/api';
import EditableFooter from './EditableFooter';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AUTH_USER_CHANGED_EVENT, getAuthUser, clearAuthToken } from '../lib/api';
import { ToastContainer } from './Toast';

type MainLayoutProps = {
  children: ReactNode;
};

const navItems = [
  { to: '/', label: 'Trang chủ' },
  { to: '/products', label: 'Sản phẩm' },
  { to: '/bmcu', label: 'BMCU Center' },

  { to: '/news', label: 'Tin tức & Hướng dẫn' },
  { to: '/about', label: 'Giới thiệu' },
];

import { useEffect, useRef, useState } from 'react';
import { apiRequest } from '../lib/api';

export default function MainLayout({ children }: MainLayoutProps) {
  const [user, setUser] = useState(getAuthUser);
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  const lastTrackedPath = useRef<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  const hasAccountName = Boolean(user?.name?.trim());
  const accountName = user?.name?.trim() || user?.email || '';
  const avatarLabel = accountName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  useEffect(() => {
    if (lastTrackedPath.current === location.pathname) return;
    lastTrackedPath.current = location.pathname;

    // Fire and forget tracking
    fetch(`${API_BASE}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: location.pathname })
    }).catch(e => console.error('Tracking failed', e));
    setIsMenuOpen(false); // Close menu on route change
    setIsAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeAccountMenu = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeAccountMenu);
    return () => document.removeEventListener('mousedown', closeAccountMenu);
  }, []);

  useEffect(() => {
    const syncUser = () => setUser(getAuthUser());
    window.addEventListener(AUTH_USER_CHANGED_EVENT, syncUser);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, syncUser);
  }, []);

  const [logo, setLogo] = useState('/images/logo/LogoShop.png');


  const handleLogout = () => {
    clearAuthToken();
    setIsAccountMenuOpen(false);
    navigate('/');
  };

  return (
    <div className="page-shell">
      <header className="topbar">
        <div className="container" style={{ position: 'relative' }}>
          <div className="nav-wrap">
            <div className="nav-header">
              <Link to="/" className="brand" aria-label="3DSmart home">
                <img src={logo} alt="3DSmart logo" className="brand-logo" style={{ maxHeight: '44px', objectFit: 'contain' }} />
              </Link>
              <button className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {isMenuOpen ? (
                    <>
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </>
                  ) : (
                    <>
                      <line x1="3" y1="12" x2="21" y2="12"></line>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <line x1="3" y1="18" x2="21" y2="18"></line>
                    </>
                  )}
                </svg>
              </button>
            </div>

            <nav className={`nav ${isMenuOpen ? 'open' : ''}`} aria-label="Main navigation">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
                >
                  {item.label}
                </NavLink>
              ))}

              {!user && (
                <NavLink to="/login" className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}>
                  Đăng nhập
                </NavLink>
              )}

              {user && user.role !== 'ADMIN' && (
                <div className="account-menu" ref={accountMenuRef}>
                  <button
                    type="button"
                    className="account-menu-trigger"
                    onClick={() => setIsAccountMenuOpen((open) => !open)}
                    aria-haspopup="menu"
                    aria-expanded={isAccountMenuOpen}
                    aria-label="Mở menu tài khoản"
                  >
                    <span className="account-avatar" aria-hidden="true">{avatarLabel}</span>
                    <span className="account-menu-trigger-text">Tài khoản</span>
                  </button>

                  {isAccountMenuOpen && (
                    <div className="account-menu-panel" role="menu">
                      <div className="account-menu-identity">
                        <span className="account-avatar account-avatar-large" aria-hidden="true">{avatarLabel}</span>
                        <div>
                          <strong>{hasAccountName ? user.name : user.email}</strong>
                          {hasAccountName && <span>{user.email}</span>}
                        </div>
                      </div>
                      <NavLink to="/account" className="account-menu-item" role="menuitem">
                        Thông tin tài khoản
                      </NavLink>
                      <div className="account-menu-divider" />
                      <button type="button" className="account-menu-item account-menu-logout" role="menuitem" onClick={handleLogout}>
                        Đăng xuất
                      </button>
                    </div>
                  )}
                </div>
              )}

              {user?.role === 'ADMIN' && (
                <NavLink to="/admin" className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}>
                  Admin
                </NavLink>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main className={isLoginPage ? '' : 'container content-wrap'}>{children}</main>

      <EditableFooter />
      <ToastContainer />
    </div>
  );
}
